import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { STEPS } from "@/components/survey/questions";
import { SurveyHeader } from "@/components/survey/SurveyHeader";
import { IntroScreen } from "@/components/survey/IntroScreen";
import { QuestionScreen } from "@/components/survey/QuestionScreen";
import { TransitionScreen } from "@/components/survey/TransitionScreen";
import { VolunteerIntroScreen } from "@/components/survey/VolunteerIntroScreen";
import { ContactForm } from "@/components/survey/ContactForm";
import { SuccessScreen } from "@/components/survey/SuccessScreen";
import { SocialProof } from "@/components/survey/SocialProof";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const slideVariants = {
  enter: (dir) => ({ x: dir > 0 ? 64 : -64, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir > 0 ? -64 : 64, opacity: 0 }),
};

export default function Survey() {
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const totalSteps = STEPS.length - 1;
  const progress = submitted ? 100 : Math.round((step / totalSteps) * 100);
  const current = STEPS[step];

  const goNext = () => {
    setDir(1);
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const goBack = () => {
    setDir(-1);
    setStep((s) => Math.max(s - 1, 0));
  };
  const handleAnswer = (id, value) => {
    setAnswers((a) => ({ ...a, [id]: value }));
    setTimeout(goNext, 350);
  };

  const handleSubmit = async (contact) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/survey/submit`, { ...contact, answers });
      setSubmitted(true);
      window.scrollTo({ top: 0 });
    } catch (e) {
      if (e.response?.status === 409) {
        toast.error(e.response.data.detail);
      } else {
        toast.error("Something went wrong — please try again!");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-white text-slate-900 overflow-x-hidden">
      <div className="pointer-events-none fixed inset-x-0 bottom-0 h-64 synth-grid" aria-hidden="true" />
      <div className="pointer-events-none fixed -top-24 -right-24 h-72 w-72 rounded-full bg-cyan-100/50 blur-3xl" aria-hidden="true" />
      <div className="pointer-events-none fixed -bottom-24 -left-24 h-72 w-72 rounded-full bg-pink-100/50 blur-3xl" aria-hidden="true" />

      <SurveyHeader progress={progress} canGoBack={!submitted && step > 0} onBack={goBack} />

      <main className="relative z-10 mx-auto flex min-h-screen w-full max-w-md flex-col pt-[68px]">
        <AnimatePresence mode="wait" custom={dir}>
          {submitted ? (
            <motion.div
              key="success"
              className="flex flex-1 flex-col"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35 }}
            >
              <SuccessScreen />
            </motion.div>
          ) : (
            <motion.div
              key={current.id}
              className="flex flex-1 flex-col"
              custom={dir}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
              {current.type === "intro" && <IntroScreen onStart={goNext} />}
              {current.type === "volunteer_intro" && <VolunteerIntroScreen onNext={goNext} />}
              {current.type === "transition" && <TransitionScreen onNext={goNext} />}
              {current.type === "choice" && (
                <QuestionScreen
                  step={current}
                  stepNumber={step}
                  totalSteps={totalSteps}
                  value={answers[current.id]}
                  onAnswer={(v) => handleAnswer(current.id, v)}
                />
              )}
              {current.type === "contact" && <ContactForm onSubmit={handleSubmit} submitting={submitting} />}
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      <SocialProof />
    </div>
  );
}
